// src/utils/deviceFingerprint.ts

// Device fingerprint utility for detecting different devices

export async function generateDeviceFingerprint(): Promise<string> {
  const components: string[] = [];

  // User Agent
  components.push(navigator.userAgent);

  // Screen resolution
  components.push(`${screen.width}x${screen.height}x${screen.colorDepth}`);

  // Timezone
  components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Language
  components.push(navigator.language);

  // Platform
  components.push(navigator.platform);

  // Hardware concurrency (CPU cores)
  components.push(String(navigator.hardwareConcurrency || 0));

  // Device memory (if available)
  const nav = navigator as any;
  if (nav.deviceMemory) {
    components.push(String(nav.deviceMemory));
  }

  // Touch support
  components.push(String("ontouchstart" in window || nav.maxTouchPoints > 0));

  // Canvas fingerprint
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.textBaseline = "top";
      ctx.font = "14px Arial";
      ctx.fillStyle = "#f60";
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = "#069";
      ctx.fillText("Talison", 2, 15);

      components.push(canvas.toDataURL().slice(0, 100));
    }
  } catch {
    // ignore
  }

  // WebRTC support check
  try {
    const connection =
      (window as any).RTCPeerConnection ||
      (window as any).webkitRTCPeerConnection;

    if (connection) {
      components.push("webrtc-supported");
    }
  } catch {
    // ignore
  }

  // Create raw fingerprint string
  const rawFingerprint = components.join("|");

  // Hash fingerprint safely
  let hashHex: string;

  if (window.crypto?.subtle) {
    const hashBuffer = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(rawFingerprint)
    );

    const hashArray = Array.from(new Uint8Array(hashBuffer));

    hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } else {
    // fallback for environments without crypto.subtle
    hashHex = btoa(rawFingerprint)
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, 64);
  }

  return hashHex.slice(0, 32);
}

// Get stored fingerprint or create a new one
export async function getDeviceFingerprint(): Promise<string> {
  const stored = localStorage.getItem("device_fingerprint");

  if (stored) {
    return stored;
  }

  const fingerprint = await generateDeviceFingerprint();

  localStorage.setItem("device_fingerprint", fingerprint);

  return fingerprint;
}

// Clear fingerprint (used for testing)
export function clearDeviceFingerprint(): void {
  localStorage.removeItem("device_fingerprint");
}